import {
  ArrowDownIcon,
  CheckIcon,
  EditIcon,
  FileEditAltIcon,
  Header,
  IconButton,
  TextInput,
  TrashCanIcon,
} from "@datagame/ui";
import { ChangeEvent, KeyboardEventHandler, useEffect, useRef, useState } from "react";
import { object, string } from "yup";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { DeleteGameModal } from "../../../../../../shared/modals/DeleteGameModal";
import { Game } from "../../../../../../../gql/graphql";
import { GAME_IMAGE_ATTRS } from "../../../../../../../constants";
import { GAME_UPDATE } from "../../../../../../../graphql/mutations/GameMutation";
import { getValidationErrors } from "../../../../../../../utils/validation/getValidationErrors";
import { isQuickPickGame } from "../../../../../../../graphql/helpers/isQuickPickGame";
import { QuickPickGameKind } from "../AddGameForm/AddGameForm";

import styles from "./GameListItem.module.scss";
import { isWordleGame } from "../../../../../../../graphql/helpers/isWordleGame";

const cx = classNames.bind(styles);

export const GameListItem = ({ game }: { game: Game }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t: tCommon } = useTranslation("common");
  const { t: tProject } = useTranslation("project");
  const [isError, setIsError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [updateGame] = useMutation(GAME_UPDATE, {
    variables: {
      gameId: game.id,
      data: {
        name,
      },
    },
  });

  const validationSchema = object({
    name: string().required(tProject("addGames.add.nameError") as string),
  });

  useEffect(() => {
    if (isEditing && inputRef.current) {
      setName(game.name);
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => setName(e.target.value);

  const handleEnter: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") {
      handleUpdateGame();
    }
  };

  const handleUpdateGame = async () => {
    const validationErrors = await getValidationErrors<{ name: string }>({ name }, validationSchema);
    setIsError(Boolean(validationErrors));

    if (validationErrors) {
      return;
    }

    await updateGame();
    setIsEditing(false);
  };

  const renderItemContent = () => {
    if (isEditing) {
      return (
        <TextInput
          ref={inputRef}
          icon={<FileEditAltIcon />}
          error={isError}
          size="small"
          onKeyPress={handleEnter}
          value={name}
          onChange={handleNameChange}
        />
      );
    }
    return (
      <Header className={styles.name} variant="h6">
        {game.name}
      </Header>
    );
  };

  const renderSubmitButton = () => {
    if (isEditing) {
      return (
        <IconButton className={styles.editToggle} icon={<CheckIcon />} onClick={handleUpdateGame} style="outline" />
      );
    }
    return <IconButton className={styles.editToggle} icon={<EditIcon />} onClick={() => setIsEditing(true)} />;
  };

  const gameKind = isQuickPickGame(game) ? QuickPickGameKind.QuickPick : game.kind;

  return (
    <>
      <div className={cx("gameListItem", { edit: isEditing })}>
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img className={cx("kindImage", { wordle: isWordleGame(game) })} {...GAME_IMAGE_ATTRS[gameKind]} />

        <div className={styles.info}>
          <Header className={styles.kindName} variant="h6">
            {tCommon(`GameKind.${gameKind}`)}
            {isEditing && <ArrowDownIcon className={styles.pointDown} />}
          </Header>
          {renderItemContent()}
        </div>
        <div className={styles.actions}>
          {renderSubmitButton()}
          <IconButton onClick={() => setIsDeleting(true)} className={styles.delete} icon={<TrashCanIcon />} />
        </div>
      </div>
      {isDeleting && <DeleteGameModal game={game} onClose={() => setIsDeleting(false)} />}
    </>
  );
};
